import {Injectable} from '@angular/core';
import {Apollo} from 'apollo-angular';
import {combineLatest, Observable, of, ReplaySubject, Subscription} from 'rxjs';
import {catchError, distinctUntilChanged, map, startWith, switchMap, take} from 'rxjs/operators';
import {ActivatedRoute, ActivatedRouteSnapshot, Params} from '@angular/router';
import {HttpHeaders} from '@angular/common/http';
import {VtkResult} from '../../../services/vtk.service';
import {VTK_JOB_ORG} from '../../../queries-workflow';
import {Query as WorkflowQuery} from '../../../types-workflow';

/** One job route's lookup: `org` is null while it runs, then the organization or why there is none. */
export interface JobOrg {
  /** The route's /o/:organization_id: whose PO it is, and whom the lookup is asked as. */
  organizationId: string;
  poName: string;
  poItemId: string;
  jobId: string;
  org: VtkResult<string> | null;
}

/** All params from the root down: the job's own and the /o/:organization_id above it. */
function allParams(route: ActivatedRouteSnapshot): Params {
  return Object.assign({}, ...route.pathFromRoot.map(r => r.params));
}

/**
 * The VTK organization a job runs in, from the one place that records it: the PO item's
 * `vtk_jobs[].organization_urn` (VTK-3586), via the slim VTK_JOB_ORG query. The job pages used to take it from an `org_urn` query
 * parameter -- a second source that a typed, shared or reloaded link did not carry, leaving the page
 * on "Loading ..." with no request made. A link that still carries it is fine; it is ignored.
 *
 * This is the organization that OWNS the job and goes into VTK's /o/{org}/ path. The organization the
 * user acts as -- the `x-castlabs-organization` header, "view as" -- is the route's /o/:organization_id;
 * a parent-org operator thus views a sub-org's job. Neither stands in for the other: an unknown owner
 * is an error, never the selected organization.
 *
 * The organization to ask as is the route's own /o/:organization_id, read from the route and part of
 * the job's identity. ContextService is not used: it updates on NavigationEnd, after the resolver ran,
 * and it is set only once the route is known -- so it could still name the organization being left.
 */
@Injectable({providedIn: 'root'})
export class VtkJobOrgService {
  constructor(private apollo: Apollo) {
  }

  /** The lookup last started, kept so that the page reuses what the route's prefetch began. */
  private last?: {key: string; result: ReplaySubject<VtkResult<string>>; subscription: Subscription};

  /**
   * Starts the lookup as soon as the route is known (the routes' `resolve`), not once the page renders:
   * the organization shell shows its pages only after the permissions and airlines have loaded, which
   * on a cold load put the whole lookup behind them.
   */
  prefetch(route: ActivatedRouteSnapshot): boolean {
    const p = allParams(route);
    this.orgUrn(p['organization_id'], p['po'], p['poitem'], p['job_id']);
    return true;
  }

  /**
   * The job route's organization, PO, PO item and job together with the job's VTK organization.
   * Bundled so that VTK requests only ever pair a job with the organization looked up for it: a new job
   * -- or the same job path under another organization -- first emits `org: null`, which ends the
   * previous requests, and a failed lookup never falls back to an older one.
   */
  jobOrgs(route: ActivatedRoute): Observable<JobOrg> {
    return combineLatest(route.pathFromRoot.map(r => r.params)).pipe(
      map(ps => Object.assign({}, ...ps) as Params),
      map(p => ({
        organizationId: p['organization_id'] as string,
        poName: p['po'] as string,
        poItemId: p['poitem'] as string,
        jobId: p['job_id'] as string,
      })),
      distinctUntilChanged((a, b) => a.organizationId === b.organizationId && a.poName === b.poName
        && a.poItemId === b.poItemId && a.jobId === b.jobId),
      switchMap(job => this.orgUrn(job.organizationId, job.poName, job.poItemId, job.jobId).pipe(
        map((org): JobOrg => ({...job, org})),
        startWith({...job, org: null} as JobOrg),
      )),
    );
  }

  orgUrn(organizationId: string, poName: string, poItemId: string, jobId: string): Observable<VtkResult<string>> {
    const key = JSON.stringify([organizationId, poName, poItemId, jobId]);
    let last = this.last;
    if (last?.key !== key) {
      this.last?.subscription.unsubscribe();
      const result = new ReplaySubject<VtkResult<string>>(1);
      const started = {key, result, subscription: new Subscription()};
      last = this.last = started;
      last.subscription.add(this.lookup(organizationId, poName, poItemId, jobId).subscribe(r => {
        result.next(r);
        // A failed lookup is not kept: coming back to the job tries again.
        if (r.error && this.last === started) this.last = undefined;
      }));
    }
    return last.result.pipe(take(1));
  }

  private lookup(organizationId: string, poName: string, poItemId: string, jobId: string): Observable<VtkResult<string>> {
    return this.apollo.use('workflow').query<WorkflowQuery>({
      query: VTK_JOB_ORG(organizationId),
      variables: {po_name: poName, po_item_id: poItemId},
      // Not the cache: it keys list_POs on ContextService.organizationId(), which still names the organization
      // being left while the resolver runs (it updates on NavigationEnd). An in-place org switch was answered
      // from the previous organization's entry, with no request at all.
      fetchPolicy: 'no-cache',
      // Sent explicitly: the default header comes from organization(), which is not loaded yet.
      context: {headers: new HttpHeaders({'X-Castlabs-Organization': organizationId})},
    }).pipe(
      map((r): VtkResult<string> => {
        const item = r.data?.list_POs?.pos?.[0]?.poitems?.[0];
        if (!item) {
          return {error: `PO item ${poItemId} of ${poName} was not found.`};
        }
        const urn = item.vtk_jobs?.find(j => j?.id === jobId)?.organization_urn;
        return urn ? {value: urn} : {error: `PO item ${poItemId} does not record the organization of VTK job ${jobId}.`};
      }),
      catchError(e => of({error: `Could not look up the organization of VTK job ${jobId}: ${e?.message ?? e}`})),
    );
  }
}
